import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Alert, Space, Divider, Flex } from "antd";
import { ArrowLeftOutlined, CheckCircleFilled, ClockCircleOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { showLoader } from "../../utils/showloader";
import VeoliaLogo from "../layout/assets/VeoliaLogo";
import { sendPasswordResetOTP, verifyPasswordResetOTP, resetPassword } from "../../services/authServices.js";
import { useTenant } from "../../context/TenantContext.jsx";
import {
  DEFAULT_MIN_PASSWORD_LENGTH,
  buildPasswordRules,
  resolveMinPasswordLength,
} from "../../utils/passwordPolicy.js";
import { useTranslation } from 'react-i18next';

const MAX_RESENDS = 3;
const COOLDOWN_SECONDS = 120;

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useTenantNavigation();
  const location = useLocation();
  const { tenantName, companyName, themeColor, companyLogo } = useTenant();
  const [logoError, setLogoError] = useState(false);

  // Set when the login screen bounced the user here for an expired password.
  const isPasswordExpired = location.state?.passwordExpired === true;

  const [step, setStep] = useState(0); // 0 email, 1 otp, 2 reset
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [resendsLeft, setResendsLeft] = useState(MAX_RESENDS);
  const [passwordForm] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  // Minimum length is dictated by the backend, delivered on verify-otp.
  const [minPasswordLength, setMinPasswordLength] = useState(DEFAULT_MIN_PASSWORD_LENGTH);

  // Prefill the email the user already typed on the login screen.
  useEffect(() => {
    const forwardedEmail = location.state?.email;
    if (!forwardedEmail) return;
    setEmail(forwardedEmail.trim());
    emailForm.setFieldsValue({ email: forwardedEmail.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.email]);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => setCooldown((c) => Math.max(c - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const passwordValue = Form.useWatch("password", passwordForm) || "";

  const passwordChecklist = useMemo(
    () => buildPasswordRules(minPasswordLength, t).map((rule) => ({ ...rule, ok: rule.test(passwordValue) })),
    [passwordValue, minPasswordLength, t]
  );

  const allRulesPass = passwordChecklist.every((r) => r.ok);

  const handleRequestOtp = async () => {
    setSuccessMessage("");
    setErrorMessage("");
    const values = await emailForm.validateFields();
    setEmail(values.email.trim());
    showLoader(true);
    try {
      const resp = await sendPasswordResetOTP(values.email);

      if (resp.success && resp.otpToken) {
        setOtpToken(resp.otpToken);
        setSuccessMessage(resp.message || "If the email exists, we sent an OTP.");
        setStep(1);
        setCooldown(COOLDOWN_SECONDS);
        setResendsLeft(MAX_RESENDS);
      } else {
        setErrorMessage(resp.message || "Unable to send OTP. Please try again.");
      }
    } catch (err) {
      setErrorMessage("Unable to send OTP. Please try again.");
    } finally {
      showLoader(false);
    }
  };

  const handleVerifyOtp = async () => {
    setSuccessMessage("");
    setErrorMessage("");
    const values = await otpForm.validateFields();
    setOtp(values.otp.trim());
    showLoader(true);
    try {
      const resp = await verifyPasswordResetOTP(email, values.otp, otpToken);
      if (resp.success) {
        setMinPasswordLength(resolveMinPasswordLength(resp.passwordLength));
        setSuccessMessage(resp.message || "OTP verified. Set your new password.");
        setStep(2);
      } else {
        setErrorMessage(resp.message || "Invalid OTP.");
      }
    } catch (err) {
      setErrorMessage("Invalid or expired OTP.");
    } finally {
      showLoader(false);
    }
  };

  const handleResetPassword = async () => {
    setSuccessMessage("");
    setErrorMessage("");
    const values = await passwordForm.validateFields();
    showLoader(true);
    try {
      const resp = await resetPassword(email, values.password, values.confirm, otpToken);
      if (resp.success) {
        setSuccessMessage(resp.message || "Password updated. You can now sign in.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setErrorMessage(resp.message || "Unable to reset password.");
      }
    } catch (err) {
      setErrorMessage("Unable to reset password. Please try again.");
    } finally {
      showLoader(false);
    }
  };

  const handleResend = async () => {
    if (cooldown || resendsLeft <= 0) return;
    setSuccessMessage("");
    setErrorMessage("");
    showLoader(true);
    try {
      const resp = await sendPasswordResetOTP(email);
      if (resp.success) {
        setOtpToken(resp.otpToken);
        setSuccessMessage(resp.message || "OTP resent.");
        setResendsLeft((c) => Math.max(c - 1, 0));
        setCooldown(COOLDOWN_SECONDS);
      } else {
        setErrorMessage(resp.message || "Unable to resend now.");
      }
    } catch (err) {
      setErrorMessage("Unable to resend now.");
    } finally {
      showLoader(false);
    }
  };

  const getStepTitle = () => {
    if (step === 0) return isPasswordExpired ? "Your Password Has Expired" : "Reset Your Password";
    if (step === 1) return "Verify OTP";
    return "Set New Password";
  };

  const getStepSubtitle = () => {
    if (step === 0 && isPasswordExpired) return "Confirm your email to receive a verification code and set a new password.";
    if (step === 0) return "Enter your email to receive a verification code.";
    if (step === 1) return `We sent a 6-digit code to ${email}.`;
    return "Create a strong password for your account.";
  };

  const renderEmailStep = () => (
    <Form form={emailForm} layout="vertical" onFinish={handleRequestOtp} className="auth-form">
      <Form.Item
        label={t('Email')}
        name="email"
        rules={[
          { required: true, whitespace: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email" },
        ]}
      >
        <Input
          placeholder="email@company.com"
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor }}
          autoComplete="email"
        />
      </Form.Item>
      <Button
        size="large"
        htmlType="submit"
        block
        className="auth-button"
        type="primary"
        style={{ backgroundColor: themeColor }}
      >
        {t('Send OTP')}
      </Button>
      <div className="text-center" style={{ marginTop: 16 }}>
        <span
          className="auth-link"
          onClick={() => navigate("/login")}
        >
          <ArrowLeftOutlined style={{ marginRight: 6 }} />
          {t('Back to Login')}
        </span>
      </div>
    </Form>
  );

  const renderOtpStep = () => (
    <Form form={otpForm} layout="vertical" onFinish={handleVerifyOtp} className="auth-form">
      <Form.Item
        label={t('Enter 6-digit code')}
        name="otp"
        rules={[
          { required: true, whitespace: true, message: "OTP is required" },
          { len: 6, message: "Enter the 6-digit code" },
          { pattern: /^\d+$/, message: "Digits only" },
        ]}
      >
        <Input
          inputMode="numeric"
          maxLength={6}
          placeholder="••••••"
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor, textAlign: "center", letterSpacing: "0.35em", fontSize: 20 }}
          autoComplete="one-time-code"
        />
      </Form.Item>

      <Flex align="center" justify="center" gap={8} style={{ marginBottom: 16 }}>
        <ClockCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
        <span style={{ color: "rgba(0, 0, 0, 0.65)", fontSize: 14 }}>
          {cooldown ? `Resend in ${cooldown}s` : "You can resend now"}
        </span>
        <Divider type="vertical" />
        <span style={{ color: "rgba(0, 0, 0, 0.65)", fontSize: 14 }}>
          {resendsLeft} resend{resendsLeft === 1 ? "" : "s"} left
        </span>
      </Flex>

      <Flex gap={12}>
        <Button
          size="large"
          className="auth-button"
          disabled={!!cooldown || resendsLeft <= 0}
          onClick={handleResend}
          style={{ flex: 1, borderColor: themeColor, color: themeColor }}
        >
          {t('Resend OTP')}
        </Button>
        <Button
          size="large"
          htmlType="submit"
          className="auth-button"
          type="primary"
          style={{ flex: 1, backgroundColor: themeColor }}
        >
          {t('Verify OTP')}
        </Button>
      </Flex>

      <div className="text-center" style={{ marginTop: 16 }}>
        <span
          className="auth-link"
          onClick={() => setStep(0)}
        >
          <ArrowLeftOutlined style={{ marginRight: 6 }} />
          {t('Change email')}
        </span>
      </div>
    </Form>
  );

  const renderResetStep = () => (
    <Form form={passwordForm} layout="vertical" onFinish={handleResetPassword} className="auth-form">
      <Form.Item
        label={t('New Password')}
        name="password"
        rules={[{ required: true, whitespace: true, message: "Password is required" }]}
      >
        <Input.Password
          placeholder={t('Enter new password')}
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor }}
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item
        label={t('Confirm Password')}
        name="confirm"
        dependencies={["password"]}
        rules={[
          { required: true, whitespace: true, message: "Confirm your password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
      >
        <Input.Password
          placeholder={t('Re-enter new password')}
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor }}
          autoComplete="new-password"
        />
      </Form.Item>

      <div style={{ background: "#f5f5f5", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ color: "rgba(0, 0, 0, 0.85)", fontWeight: 600, marginBottom: 8 }}>{t('Password must include:')}</div>
        <Space direction="vertical" size={4}>
          {passwordChecklist.map((rule) => (
            <div key={rule.key} style={{ fontSize: 14, color: rule.ok ? "#52c41a" : "rgba(0, 0, 0, 0.45)" }}>
              {rule.ok ? <CheckCircleFilled style={{ marginRight: 8 }} /> : <span style={{ marginRight: 8 }}>•</span>}
              {rule.label}
            </div>
          ))}
        </Space>
      </div>

      <Button
        size="large"
        htmlType="submit"
        block
        className="auth-button"
        type="primary"
        style={{ backgroundColor: themeColor }}
        disabled={!allRulesPass}
      >
        {t('Update Password')}
      </Button>

      <div className="text-center" style={{ marginTop: 16 }}>
        <span
          className="auth-link"
          onClick={() => setStep(1)}
        >
          <ArrowLeftOutlined style={{ marginRight: 6 }} />
          {t('Back to OTP')}
        </span>
      </div>
    </Form>
  );

  return (
    <Flex className="layoutStyle" justify="center">
      {/* Left side - same as login page */}
      <div className="leftSider" style={{ backgroundImage: "url('/assets/LoginBackground.png')", backgroundRepeat: "no-repeat" }}>
        <div className="top-logo">
          {companyLogo && !logoError ? (
            <img
              src={companyLogo}
              alt={t('Company Logo')}
              style={{ maxWidth: "176px", maxHeight: "60px" }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <VeoliaLogo type="small" />
          )}
        </div>
        <div className="middle-section">
          <img src="/assets/LoginImg1.svg" style={{ width: "11rem", height: "11rem" }} alt="" />
          <img src="/assets/LoginImg2.svg" className="center-image" style={{ width: "13.375rem", height: "12.25rem" }} alt="" />
          <img src="/assets/LoginImg3.svg" style={{ width: "16rem", height: "10rem" }} alt="" />
        </div>
        <div className="bottom-section">
          <div className="login-title" style={{ color: themeColor }}>{t('Forgot Password?')}</div>
          <div className="login-subtitle">{t("Don't worry, we'll help you reset your password at")} {companyName} securely.</div>
        </div>
      </div>

      {/* Right side - form area */}
      <div className="rightSider">
        <div className="auth-panel">
          <div className="auth-header">
            <h1 className="auth-title">{getStepTitle()}</h1>
            <p className="auth-subtitle">{getStepSubtitle()}</p>
          </div>

          {isPasswordExpired && (
            <Alert
              type="warning"
              message="Your password has expired"
              description={t('For security reasons your password must be renewed. Reset it now to regain access to your account.')}
              showIcon
            />
          )}
          {successMessage && (
            <Alert type="success" message={successMessage} showIcon />
          )}
          {errorMessage && (
            <Alert type="error" message={errorMessage} showIcon />
          )}

          <div>
            {step === 0 && renderEmailStep()}
            {step === 1 && renderOtpStep()}
            {step === 2 && renderResetStep()}
          </div>
        </div>
      </div>
    </Flex>
  );
};

export default ForgotPassword;
